import React, { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown, Clock, MapPin, Package } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout/DasboardLayout";
import { API_URL } from "./config";
const TripDisplay = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrips = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/trip`
        );
        setTrips(response.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrips();
  }, []);

  const getTripStats = () => {
    const totalTrips = trips.length;
    const completedTrips = trips.filter(
      (trip) => trip.status === "Trip Ended"
    ).length;
    const totalCost = trips.reduce(
      (acc, trip) => acc + Number(trip.delivery_cost_proposed),
      0
    );
    const averageCost = totalCost / totalTrips || 0;
    const totalDistance = trips.reduce(
      (acc, trip) => acc + Number(trip.distance),
      0
    );

    return {
      totalTrips,
      completedTrips,
      totalCost,
      averageCost,
      totalDistance,
    };
  };

  if (loading)
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#ffc000] border-t-[#002966]"></div>
        </div>
      </DashboardLayout>
    );

  if (error)
    return (
  <DashboardLayout>
      <div className="text-red-600 text-center p-4">Error: {error.message}</div></DashboardLayout>
    );

  const stats = getTripStats();

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="container  p-6">
          <h1 className="text-3xl font-bold mb-8 text-[#002966]">
            Trip Dashboard
          </h1>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-[#ffc000]">
              <div className="flex items-center">
                <Package className="h-8 w-8 text-[#002966]" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Trips</p>
                  <p className="text-2xl font-bold text-[#002966]">
                    {stats.totalTrips}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-[#ffc000]">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-[#002966]" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Completed Trips</p>
                  <p className="text-2xl font-bold text-[#002966]">
                    {stats.completedTrips}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-[#ffc000]">
              <div className="flex items-center">
                <MapPin className="h-8 w-8 text-[#002966]" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Distance</p>
                  <p className="text-2xl font-bold text-[#002966]">
                    {stats.totalDistance.toFixed(2)} km
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-[#ffc000]">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-[#002966]" />
                <div className="ml-4">
                  <p className="text-sm text-gray-600">Avg. Cost</p>
                  <p className="text-2xl font-bold text-[#002966]">
                    {stats.averageCost.toFixed(2)} {trips[0]?.currency_symbol}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trip List */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#002966] text-white">
                    <th className="px-6 py-3 text-left">Trip ID</th>
                    <th className="px-6 py-3 text-left">Status</th>
                    <th className="px-6 py-3 text-left">Route</th>
                    <th className="px-6 py-3 text-left">Distance</th>
                    <th className="px-6 py-3 text-left">Cost</th>
                    <th className="px-6 py-3 text-left">Request Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {trips.map((trip) => (
                    <tr
                      key={trip.trip_id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 font-medium text-[#002966]">
                        #{trip.trip_id}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-sm ${
                            trip.status === "completed"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {trip.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <span className="text-gray-600">
                            {trip.origin_location}
                          </span>
                          <TrendingDown className="h-4 w-4 mx-2 text-[#ffc000]" />
                          <span className="text-gray-600">
                            {trip.dest_location}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">{trip.distance} km</td>
                      <td className="px-6 py-4">
                        {trip.delivery_cost_proposed} {trip.currency_symbol}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(
                          trip.request_start_datetime
                        ).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TripDisplay;
